import { type JSONContent } from "@tiptap/core"

export type RichContentDocument = JSONContent

const rasterImageDataUrlPattern = /^data:image\/(?:avif|bmp|gif|jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/i

export function isSafeRasterImageDataUrl(value: unknown): value is string {
	return typeof value === "string" && rasterImageDataUrlPattern.test(value)
}

const richContentNodeTypes = new Set([
	"blockquote",
	"bulletList",
	"bunnyVideo",
	"codeBlock",
	"doc",
	"hardBreak",
	"heading",
	"horizontalRule",
	"image",
	"linkPreview",
	"listItem",
	"orderedList",
	"paragraph",
	"richEmbed",
	"table",
	"tableCell",
	"tableHeader",
	"tableRow",
	"taskItem",
	"taskList",
	"text",
	"youtube",
])
const richContentMarkTypes = new Set(["bold", "code", "highlight", "italic", "link", "strike", "underline"])

export function isTiptapDocument(value: unknown): value is RichContentDocument {
	return isRecord(value) && value.type === "doc" && isTiptapNode(value)
}

export function parseTiptapDocument(value: string | null): RichContentDocument | null {
	if (value === null) return null
	try {
		const parsed: unknown = JSON.parse(value)
		return isTiptapDocument(parsed) ? parsed : null
	} catch {
		return null
	}
}

function isTiptapNode(value: unknown): boolean {
	if (!isRecord(value) || typeof value.type !== "string" || !richContentNodeTypes.has(value.type)) return false
	if (value.type === "text" && typeof value.text !== "string") return false
	if ("content" in value && (!Array.isArray(value.content) || !value.content.every(isTiptapNode))) return false
	if (
		"marks" in value &&
		(!Array.isArray(value.marks) ||
			!value.marks.every(
				mark => isRecord(mark) && typeof mark.type === "string" && richContentMarkTypes.has(mark.type),
			))
	) {
		return false
	}
	return !("attrs" in value && !isRecord(value.attrs))
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null
}
